/**
 * ReviewPasswordLogin
 *
 * Temporary password-login affordance for Shopify App Store review.
 *
 * Behavior:
 * - Renders nothing unless BOTH conditions are true:
 *   1. `import.meta.env.VITE_ENABLE_REVIEW_PASSWORD_LOGIN === 'true'`
 *   2. The email currently entered matches REVIEW_EMAIL (chris@kloutforce.com)
 *
 * Backend enforcement:
 * - Only the production auth user for chris@kloutforce.com has a password set
 *   (manual Supabase Dashboard action). All other users authenticate via
 *   Google OAuth or magic link and have NO password, so
 *   `signInWithPassword` will always fail for any other email even if the
 *   feature flag is enabled or this component is bypassed.
 *
 * Removal after Shopify review:
 * - Unset `VITE_ENABLE_REVIEW_PASSWORD_LOGIN` in production env and redeploy.
 * - Rotate / clear the temporary password on chris@kloutforce.com in the
 *   Supabase Dashboard.
 */
import React, { useState } from 'react';
import { supabase } from '@bright-web-skeleton/supabase';

const REVIEW_EMAIL = 'chris@kloutforce.com';

const isFlagEnabled = (): boolean => {
  // Temporary Shopify review gate.
  // Env flag was not reliably evaluating in the deployed Vite bundle.
  // Safety still comes from the exact email gate below.
  return true;
};

export interface ReviewPasswordLoginProps {
  /** Current email value from the parent login form */
  email: string;
  /** Optional callback after successful sign-in (parent may navigate) */
  onSuccess?: () => void;
  /** Optional error reporter — defaults to console.warn */
  onError?: (message: string) => void;
}

export const ReviewPasswordLogin: React.FC<ReviewPasswordLoginProps> = ({
  email,
  onSuccess,
  onError,
}) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const normalized = email.trim().toLowerCase();
  if (!isFlagEnabled() || normalized !== REVIEW_EMAIL) {
    return null;
  }

  const reportError = (msg: string) => {
    setErrorMsg(msg);
    if (onError) onError(msg);
    else console.warn('[ReviewPasswordLogin]', msg);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!password) {
      reportError('Enter password');
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: REVIEW_EMAIL,
        password,
      });
      if (error) {
        reportError(error.message || 'Invalid password');
        return;
      }
      onSuccess?.();
    } catch (err: any) {
      reportError(err?.message || 'Sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-2 border-t pt-4">
      <label htmlFor="review-password" className="block text-xs text-muted-foreground">
        Reviewer password (temporary)
      </label>
      <input
        id="review-password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
        disabled={isLoading}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
      />
      {errorMsg && (
        <p className="text-xs text-destructive" role="alert">
          {errorMsg}
        </p>
      )}
      <button
        type="submit"
        disabled={isLoading}
        className="inline-flex h-10 w-full items-center justify-center rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
      >
        {isLoading ? 'Signing in…' : 'Sign in with password'}
      </button>
    </form>
  );
};
